import Axios from "axios";
import { Dispatch } from "redux";
import { CLEAR_CODED_VALUES_TOKEN, CREATE_CODED_VALUE, DELETE_CODED_VALUES, GET_CODED_TYPES, GET_CODED_VALUES, UPDATE_CODED_VALUE } from "state/constants/action-types";
import { CodedValue } from "state/reducers/CodedValueReducer";
import { FixMeLater } from "types/FixMeLaterType";

import { getApiConfig } from "../../config/apiConfig";
import { adalApiFetch } from "../../config/azureConfig";

type Callback = (success: boolean) => void

const API_CONFIG = getApiConfig();

enum GET_CODED_VALUE_SIZE {
    SMALL = 20,
    MEDIUM = 50,
    LARGE = 100
}

export const getCodedValues = (
    callback: Callback,
    pageSize: number | null = GET_CODED_VALUE_SIZE.SMALL,
    codeType: string[] | null = null,
    ids: string[] | null = [],
    continuationToken: string | null = null,
    exhaust: boolean | null = false
) => (dispatch: Dispatch) => {

    const headers = continuationToken != null ? {
        "x-pace-continuation-token": continuationToken
    } : undefined

    const paramsString = `?pageSize=${pageSize}`
    const params = new URLSearchParams(paramsString)

    if (!!codeType) {
        // Split depending on whether or not codeType is currently an array. 
        // RuleCard usages rely on this being able to be an array, whereas CodedValues filtering does not.
        if (Array.isArray(codeType)) {
            codeType.forEach(cType => {
                params.append("codeType", cType)
            })
        } else {
            params.append("codeType", codeType)
        }
    }

    if (!!ids && ids.length > 0) {
        ids.forEach(id => {
            params.append("id", id)
        });
    }

    // If request is being instructed to exhaust, continue pinging the set until no more continuation tokens are yielded.
    if (exhaust) {
        const continueUntilExhaust = async () => {
            let shouldContinue: boolean = true;
            let continuation: any = null;
            let completeSet: FixMeLater = { CodedValues: [] };

            while (shouldContinue) {

                const headers = continuation != null ? {
                    "x-pace-continuation-token": continuation
                } : undefined

                await adalApiFetch(Axios, `${API_CONFIG.URL}/coded-values?${params.toString()}`, {
                    method: "get",
                    headers: headers
                })
                    .then((res: FixMeLater) => {
                        if (res.data.ContinuationToken != undefined) {
                            shouldContinue = true;
                            continuation = res.data.ContinuationToken;
                        } else {
                            shouldContinue = false;
                        }
                        completeSet.CodedValues = completeSet.CodedValues.concat(res.data.CodedValues)
                    })
            }

            return completeSet;
        }

        // Run loop until set is exhausted, then dispatch it.
        continueUntilExhaust().then((res: FixMeLater) => {
            dispatch({
                type: GET_CODED_VALUES,
                payload: res
            })
            callback(true);
        });
    }

    adalApiFetch(Axios, `${API_CONFIG.URL}/coded-values?${params.toString()}`, {
        method: "get",
        headers: headers
    })
        .then((res: FixMeLater) => {
            dispatch({
                type: GET_CODED_VALUES,
                payload: res.data
            })
            callback(true);
        }).catch((error: Error) => {
            console.log(`Exception Occurred: ${error.message}`);
            callback(false);
        })
};

export const getCodeTypes = (callback: Callback) => (dispatch: Dispatch) => {
    adalApiFetch(Axios, `${API_CONFIG.URL}/code-types`, {
        method: "get"
    })
        .then((res: FixMeLater) => {
            dispatch({
                type: GET_CODED_TYPES,
                payload: res.data
            })
            callback(true);
        }).catch((error: Error) => {
            console.log(`Exception Occurred: ${error.message}`);
            callback(false);
        })
}

export const createCodedValue = (codedValue: CodedValue, callback?: Callback) => (dispatch: Dispatch) => {
    adalApiFetch(Axios, `${API_CONFIG.URL}/coded-values`, {
        method: "post",
        data: codedValue
    })
        .then((res: FixMeLater) => {
            dispatch({
                type: CREATE_CODED_VALUE,
                payload: res.data || codedValue
            })
            if (!!callback) callback(true);
        }).catch((error: Error) => {
            console.log(`Exception Occurred: ${error.message}`);
            if (!!callback) callback(false);
        })
}

export const deleteCodedValues = (codedValueIds: string[], callback: Callback) => (dispatch: Dispatch) => {

    const params = new URLSearchParams()

    if (!!codedValueIds && codedValueIds.length > 0) {
        codedValueIds.forEach(codedValueId => {
            params.append("id", codedValueId)
        });
    }

    adalApiFetch(Axios, `${API_CONFIG.URL}/coded-values?${params}`, {
        method: "delete"
    })
        .then((res: FixMeLater) => {
            dispatch({
                type: DELETE_CODED_VALUES,
                payload: codedValueIds
            })
            callback(true);
        }).catch((error: Error) => {
            console.log(`Exception Occurred: ${error.message} `);
            callback(false);
        })
}

export const updateCodedValue = (codedValue: CodedValue, callback?: Callback) => (dispatch: Dispatch) => {
    adalApiFetch(Axios, `${API_CONFIG.URL}/coded-values/${codedValue.id}`, {
        method: "put",
        data: codedValue
    })
        .then((res: FixMeLater) => {
            dispatch({
                type: UPDATE_CODED_VALUE,
                payload: {
                    newData: res.data,
                    oldId: codedValue.id
                }
            })
            if (!!callback) callback(true);
        }).catch((error: Error) => {
            console.log(`Exception Occurred: ${error.message} `);
            if (!!callback) callback(false);
        })
}

export const clearContinuationToken = (callback: Callback) => (dispatch: Dispatch) => {
    dispatch({
        type: CLEAR_CODED_VALUES_TOKEN,
        payload: null
    })
    callback(true);
}